import React from 'react';
import { Box, Typography } from '@mui/material';
import AttributeSelector from './AttributeSelector';
import { CHARACTERISTICS_METADATA } from '@gamma/shared/attributes';

/**
 * AttributeSelectorGroup - Displays multiple AttributeSelectors with shared state
 *
 * @param {Object} attributeValues - Map of attributeType → selected value
 * @param {function} onChange - Callback when any attribute changes: (attributeType, newValue) => void
 * @param {Object} availableAttributes - Map of attributeType → array of possible values
 *                                       If not provided, uses all from CHARACTERISTICS_METADATA
 * @param {number} iconSize - Size of icons (default: 40)
 * @param {boolean} disabled - Whether all selectors are disabled
 * @param {string} title - Optional title for the group
 * @param {Array<string>} attributeOrder - Optional array to control display order
 */
export default function AttributeSelectorGroup({
  attributeValues,
  onChange,
  availableAttributes,
  iconSize = 40,
  disabled = false,
  title,
  attributeOrder
}) {
  // Determine which attributes to display
  const attributesToShow = attributeOrder || Object.keys(availableAttributes || CHARACTERISTICS_METADATA);

  return (
    <Box>
      {title && (
        <Typography
          variant="overline"
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            color: 'text.secondary',
            lineHeight: 1.5,
            display: 'block',
            mb: 1
          }}
        >
          {title}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {attributesToShow.map((attributeType) => {
          const values = availableAttributes
            ? availableAttributes[attributeType]
            : Object.values(CHARACTERISTICS_METADATA[attributeType]?.enum || {});

          if (!values || values.length === 0) {
            return null;
          }

          return (
            <AttributeSelector
              key={attributeType}
              attributeType={attributeType}
              values={values}
              selectedValue={attributeValues[attributeType]}
              onChange={onChange}
              iconSize={iconSize}
              disabled={disabled}
            />
          );
        })}
      </Box>
    </Box>
  );
}
